// File: frontend/src/components/people/PersonRow.tsx

import React from "react";
import { useNavigate } from "react-router-dom";
import "./PersonRow.css";
import { PersonModel } from "../../models/PersonModel";
import { useTranslation } from "react-i18next";
import {
  FaFutbol, FaBasketballBall, FaVolleyballBall,
  FaFootballBall, FaBaseballBall, FaHockeyPuck,
  FaUser, FaLock,
} from "react-icons/fa";
import { MdSportsHandball } from "react-icons/md";
import { RoleIconByKey } from "../../utils/roleIconMap";
import { usePersonRoles } from "../../hooks/usePersonRoles";

import { personUrl } from "../../utils/canonicalUrl";
interface PersonRowProps {
  person: PersonModel;
  isSelected: boolean;
  onSelect: () => void;
  // Called on mobile after navigation so App.tsx can switch to the entries pane
  onMobileNavigate?: () => void;
}

const getInitials = (firstName: string, lastName: string): string =>
  `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}`;

// Same heat system as ClubRow/TeamRow — keeps the recentEntryCount badge
// visually consistent across every left-pane domain (Gündem/Maç/Kulüp/Kişi/Takım).
const getHeatClass = (count: number): string => {
  if (count === 0)  return "heat-0";
  if (count < 3)    return "heat-1";
  if (count < 8)    return "heat-2";
  if (count < 20)   return "heat-3";
  if (count < 50)   return "heat-4";
  return "heat-5";
};

const formatCount = (count: number): string => {
  if (count >= 1_000_000) return (count / 1_000_000).toFixed(1).replace(".", ",") + "m";
  if (count >= 1_000)     return (count / 1_000).toFixed(1).replace(".", ",") + "b";
  return String(count);
};

/**
 * Icon representing the sport branch (right of the role icon).
 */
const SportIcon: React.FC<{ sportType: string }> = ({ sportType }) => {
  const p = { size: 11 };
  switch (sportType) {
    case "FOOTBALL":          return <FaFutbol {...p} />;
    case "BASKETBALL":        return <FaBasketballBall {...p} />;
    case "VOLLEYBALL":        return <FaVolleyballBall {...p} />;
    case "AMERICAN_FOOTBALL": return <FaFootballBall {...p} />;
    case "BASEBALL":          return <FaBaseballBall {...p} />;
    case "ICE_HOCKEY":        return <FaHockeyPuck {...p} />;
    case "HANDBALL":          return <MdSportsHandball {...p} />;
    default:                  return <FaFutbol {...p} />;
  }
};

export const PersonRow: React.FC<PersonRowProps> = ({ person, isSelected, onSelect, onMobileNavigate }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // md_person_role is the source of truth for role labels and icons — the static
  // person_role_* translation keys only cover the roles that once lived in the Java enum.
  const { translateRole, getIconKey } = usePersonRoles();

  /**
   * Display label for a role code: the master-data name first, then the legacy
   * translation key, and finally the raw code so nothing renders blank.
   */
  const roleLabel = (code: string): string => {
    const mdName = translateRole(code);
    if (mdName && mdName !== code) return mdName;
    return t(`person_role_${code.toLowerCase()}`, { defaultValue: code });
  };

  // Current career entry: prefer active (endYear null), fallback to first
  // Current career entry: the most recently started open-ended stint, falling back to
  // the first row when every stint has ended. Sorting keeps the row stable for people
  // holding more than one open-ended role at the same time.
  const currentEntry = [...(person.careerEntries ?? [])]
    .filter(e => e.endYear === null)
    .sort((a, b) => b.startYear - a.startYear || b.id - a.id)[0]
    ?? person.careerEntries?.[0];

  // When no career entry exists, fall back to inferred role/sport from match assignments
  const effectiveRole      = currentEntry?.role      ?? person.inferredRole      ?? null;
  const effectiveSportType = currentEntry?.sportType ?? person.inferredSportType ?? null;

  const buildSubtitle = (): string => {
    if (!currentEntry) {
      // No career entry — show inferred role label if available
      if (effectiveRole) {
        return roleLabel(effectiveRole);
      }
      return "";
    }
    const label     = roleLabel(currentEntry.role);
    const clubName  = currentEntry.clubShortName ?? currentEntry.customClubName ?? "";
    return clubName ? `${label} · ${clubName}` : label;
  };

  const handleClick = () => {
    onSelect();
    // Navigate using slug — falls back to id-based URL only if slug is missing (legacy data)
    navigate(personUrl(person.slug, person.id));
    // On mobile: signal App.tsx to switch from the list pane to the content pane
    onMobileNavigate?.();
  };

  // Display name: knownName (e.g. "Fred") when set, otherwise fullName
  const displayName = person.knownName ?? person.fullName;

  // Recent entry count badge — always shown, same heat system as ClubRow/TeamRow
  const recentCount = person.recentEntryCount ?? 0;

  return (
    <div
      className={`person-row ${isSelected ? "selected" : ""} ${person.isLocked ? "locked" : ""}`}
      onClick={handleClick}
    >
      {/* Avatar */}
      <div className="person-row__avatar">
        {person.photoUrl ? (
          <img
            src={`${process.env.REACT_APP_API_URL || "http://localhost:8080"}${person.photoUrl}`}
            alt={displayName}
          />
        ) : (
          getInitials(person.firstName, person.lastName)
        )}
      </div>

      {/* Name + subtitle */}
      <div className="person-row__info">
        <div className="person-row__name">{displayName}</div>
        {buildSubtitle() && (
          <div className="person-row__subtitle">{buildSubtitle()}</div>
        )}
      </div>

      {/* Right side: entry badge + role/sport icons + lock icon (always rightmost) */}
      <div className="person-row__right">
        <span
          className={`person-row__entry-badge ${getHeatClass(recentCount)}`}
          title={t("person_row_recent_entries", "Son 24 saatte entry")}
        >
          {formatCount(recentCount)}
        </span>
        {/* Role + sport icons: use career entry when available, inferred values otherwise */}
        <div className="person-row__icons">
          {effectiveRole && effectiveSportType ? (
            <>
              <span
                className="person-row__icon"
                title={roleLabel(effectiveRole)}
              >
                {/* RoleIconByKey expects a react-icons key, not a role code — the code
                    has to be resolved through md_person_role first, otherwise every row
                    silently falls back to the generic user icon. */}
                <RoleIconByKey iconKey={getIconKey(effectiveRole)} size={11} />
              </span>
              <span
                className="person-row__icon"
                title={t(`menu_sport_${effectiveSportType.toLowerCase()}`, { defaultValue: effectiveSportType })}
              >
                <SportIcon sportType={effectiveSportType} />
              </span>
            </>
          ) : (
            <span className="person-row__icon" title={t("search_category_people")}>
              <FaUser size={11} />
            </span>
          )}
        </div>
        {person.isLocked && (
          <span className="row-lock-icon" title={t("topic_locked_tooltip")}>
            <FaLock size={10} />
          </span>
        )}
      </div>
    </div>
  );
};